import React, { useContext } from "react";
import { Form, useField } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { RefreshCcw, Target, ChevronLeft, ChevronRight } from "lucide-react";

import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import type { RegisterDraft } from "../types";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";

interface Props {
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
}

type FrequencyValue = "one_time" | "recurring";

const RegisterClientFrequency: React.FC<Props> = ({ client, setClient }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(client, "frequency");

    const onSubmit = async (values: Record<string, unknown>) => {
        try {
            setClient(Object.assign({}, client, values) as RegisterDraft);
            stepper.goTo("informations");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={client}
                mutators={{ ...arrayMutators }}
                validate={(values: Record<string, unknown>) => {
                    const errors: Record<string, string> = {};
                    const v = values["frequency"];
                    if (v !== "one_time" && v !== "recurring") {
                        errors.frequency = "Choisissez une fréquence pour continuer.";
                    }
                    return errors;
                }}
                render={({ handleSubmit, submitting, form, values }) => {
                    const selected = (values as Record<string, unknown>)["frequency"] as FrequencyValue | undefined;

                    return (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <div>
                                <p className="h1 font-black">
                                    À quelle fréquence{" "}
                                    <span className="block text-primary">avez-vous besoin de ce service ?</span>
                                </p>
                                <p className="h4 mt-4">Choisissez la fréquence qui vous convient.</p>
                            </div>

                            <div className="register__group-fields">
                                <div className="register__grid">
                                    <div className="grid gap-4">
                                        {(
                                            [
                                                {
                                                    value: "one_time",
                                                    title: "Besoin ponctuel",
                                                    subtitle: "Une seule intervention",
                                                    Icon: Target,
                                                },
                                                {
                                                    value: "recurring",
                                                    title: "Besoin récurrent",
                                                    subtitle: "Interventions régulières",
                                                    Icon: RefreshCcw,
                                                },
                                            ] as const
                                        ).map(({ value, title, subtitle, Icon }) => {
                                            const checked = selected === value;
                                            return (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    className={[
                                                        "flex w-full items-center justify-between gap-4 rounded-[22px] border bg-background px-6 py-5 text-left transition hover:bg-accent/40",
                                                        checked ? "border-primary bg-primary/5" : "border-input",
                                                    ].join(" ")}
                                                    role="radio"
                                                    aria-checked={checked}
                                                    onClick={() => form.change("frequency", value)}
                                                >
                                                    <div className="flex items-start gap-4">
                                                        <div
                                                            className={[
                                                                "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-colors",
                                                                checked
                                                                    ? "bg-primary/10 text-primary"
                                                                    : "bg-muted/70 text-muted-foreground/70",
                                                            ].join(" ")}
                                                        >
                                                            <Icon className="h-5 w-5" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="text-base font-black tracking-tight">
                                                                {title}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {subtitle}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className={[
                                                            "relative h-5 w-5 rounded-full border transition",
                                                            checked ? "border-primary bg-background" : "border-muted-foreground/40 bg-background",
                                                        ].join(" ")}
                                                        aria-hidden="true"
                                                    >
                                                        <span
                                                            className={[
                                                                "absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition",
                                                                checked ? "bg-primary opacity-100" : "bg-transparent opacity-0",
                                                            ].join(" ")}
                                                        />
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="text-center field__feedback error">
                                        <ShowErrorMessage />
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    type="button"
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setClient(Object.assign({}, client, values));
                                        stepper.goTo("need");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                    type="submit"
                                    disabled={submitting || (selected !== "one_time" && selected !== "recurring")}
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

const ShowErrorMessage = () => {
    return useField("frequency").meta.submitError?.props?.error.message;
};

export default RegisterClientFrequency;

